// Optimizer API service — proxies through tradecraft-ai API to strategy-engine
const API_BASE = import.meta.env.VITE_API_URL || "";

function generateCorrelationId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`;
}

interface ApiError {
  error?: string;
  message?: string;
  code?: string;
}

async function optimizerRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const authData = localStorage.getItem("tradecraft-auth");
  let token: string | null = null;
  try {
    token = authData ? JSON.parse(authData).state?.token : null;
  } catch {
    // Corrupted auth data — proceed without token (will get 401)
    token = null;
  }

  const correlationId = generateCorrelationId();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-Correlation-ID": correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    throw new Error(
      `Request failed with status ${response.status} (non-JSON response)`,
    );
  }

  if (!response.ok) {
    const error = data as ApiError;
    throw new Error(error.message || error.error || "Request failed");
  }

  return data as T;
}

// --- Type Definitions ---

export interface OptimizerDefaults {
  population_size: number;
  max_generations: number;
  max_evaluations: number;
  max_tree_depth: number;
  crossover_prob: number;
  mutation_prob: number;
  stagnation_limit: number;
  initial_capital: number;
  overfit_threshold: number;
  max_candidates: number;
  backtest_workers?: number;
  max_concurrent_jobs?: number;
}

export interface OptimizeRequest {
  strategy_code: string;
  target_metric: string;
  target_value: number;
  preserve?: Record<string, number>;
  source_metrics?: Record<string, number>;
  population_size?: number;
  max_generations?: number;
  max_evaluations?: number;
  max_tree_depth?: number;
  crossover_prob?: number;
  mutation_prob?: number;
  stagnation_limit?: number;
  initial_capital?: number;
  overfit_threshold?: number;
  max_candidates?: number;
}

export interface OptimizeResponse {
  job_id: string;
  status: string;
  tier: string;
}

// --- Admin Defaults ---

export async function getOptimizerDefaults(): Promise<OptimizerDefaults> {
  return optimizerRequest<OptimizerDefaults>(
    "/api/v1/admin/optimizer/defaults",
  );
}

export async function updateOptimizerDefaults(
  defaults: OptimizerDefaults,
): Promise<OptimizerDefaults> {
  return optimizerRequest<OptimizerDefaults>(
    "/api/v1/admin/optimizer/defaults",
    {
      method: "PUT",
      body: JSON.stringify(defaults),
    },
  );
}

// --- Job Types ---

export interface OptimizerJob {
  job_id: string;
  job_type: string;
  status: "pending" | "running" | "completed" | "failed";
  tier: string;
  user_id?: string;
  strategy_name?: string;
  generations_run?: number;
  evaluations_run?: number;
  execution_ms?: number;
  pareto_front_size?: number;
  error_message?: string;
  candidates?: OptimizerCandidate[];
  source_dsl?: string;
  source_metrics?: Record<string, number>;
  request_payload?: OptimizeRequestPayload;
  created_at: string;
  completed_at?: string;
}

export interface OptimizerCandidate {
  dsl_code: string;
  strategy_hash?: string;
  metrics: {
    cagr: number;
    sharpe: number;
    max_drawdown: number;
    sortino?: number;
  };
  oos_metrics?: {
    cagr?: number;
    sharpe?: number;
    max_drawdown?: number;
  };
  overfit_ratio?: number;
  rank?: number;
  // Legacy nested format (kept for backward compat)
  walk_forward?: {
    oos_cagr?: number;
    oos_sharpe?: number;
    overfit_ratio?: number;
    is_overfit?: boolean;
  };
}

export interface OptimizeRequestPayload {
  strategy_code: string;
  target_metric: string;
  target_value: number;
  preserve?: Record<string, number>;
  max_generations?: number;
  max_evaluations?: number;
  population_size?: number;
  max_tree_depth?: number;
  crossover_prob?: number;
  mutation_prob?: number;
  stagnation_limit?: number;
  initial_capital?: number;
  overfit_threshold?: number;
  max_candidates?: number;
}

export interface JobsListResponse {
  jobs: OptimizerJob[];
  total: number;
  page: number;
  limit: number;
}

export interface JobCountResponse {
  count: number;
}

// --- Optimization Jobs ---

export async function submitOptimization(
  request: OptimizeRequest,
): Promise<OptimizeResponse> {
  return optimizerRequest<OptimizeResponse>("/api/v1/optimizer/improve", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function listOptimizerJobs(
  params: { status?: string; page?: number; limit?: number } = {},
): Promise<JobsListResponse> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return optimizerRequest<JobsListResponse>(
    `/api/v1/optimizer/jobs${qs ? `?${qs}` : ""}`,
  );
}

export async function getOptimizerJob(jobId: string): Promise<OptimizerJob> {
  return optimizerRequest<OptimizerJob>(`/api/v1/optimizer/jobs/${jobId}`);
}

export async function getRunningJobCount(): Promise<number> {
  const resp = await optimizerRequest<JobCountResponse>(
    "/api/v1/optimizer/jobs/count?status=running",
  );
  return resp.count;
}

export async function deleteOptimizerJob(jobId: string): Promise<void> {
  await optimizerRequest(`/api/v1/optimizer/jobs/${jobId}`, {
    method: "DELETE",
  });
}
